import { motion } from 'framer-motion';
import SafeImage from './SafeImage';
import { HERO_IMAGE, IMAGE_FALLBACK_CHAIN } from '../config/siteImages';
import { COMPANY_NAME } from '../config/siteInfo';

function scrollTo(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth' });
}

const textStagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06, delayChildren: 0.04 } },
};

const fadeUp = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.45, ease: [0.25, 0.1, 0.25, 1] } },
};

const imageReveal = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.55, ease: [0.25, 0.1, 0.25, 1] } },
};

export default function Hero({ hero }) {
  const safeHero = hero || {};
  const trust = safeHero.trustChips || ['Structural steel', 'Architectural steel', 'Bespoke fabrication'];

  return (
    <section className="hero hero--premium" id="home">
      <div className="hero-grid" aria-hidden />

      <div className="container hero-layout">
        <motion.div className="hero-copy" variants={textStagger} initial="hidden" animate="show">
          <motion.div className="hero-eyebrow eyebrow" variants={fadeUp}>
            {safeHero.eyebrow || COMPANY_NAME}
          </motion.div>

          <motion.h1 className="hero-headline hero-headline--premium" variants={fadeUp}>
            {safeHero.heading || 'Precision steelwork for'}
            <span className="headline-accent">{safeHero.subheading || 'contractors & private clients'}</span>
          </motion.h1>

          <motion.p className="hero-sub" variants={fadeUp}>
            {safeHero.paragraph ||
              'Structural frames, architectural steel, and bespoke fabrication — delivered with clear communication, quality workmanship, and dependable timelines.'}
          </motion.p>

          <motion.div className="hero-actions" variants={fadeUp}>
            <button type="button" className="btn-primary btn-primary--hero" onClick={() => scrollTo('contact')}>
              {safeHero.primaryCtaLabel || 'Get a Quote'}
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>

            <button type="button" className="btn-outline btn-outline--hero" onClick={() => scrollTo('projects')}>
              {safeHero.secondaryCtaLabel || 'View Projects'}
            </button>
          </motion.div>

          <motion.div className="hero-trust hero-trust--premium" variants={fadeUp} aria-label="Services">
            {trust.map((label) => (
              <span key={label} className="hero-trust-item">
                {label}
              </span>
            ))}
          </motion.div>
        </motion.div>

        <motion.div
          className="hero-visual hero-visual--dominant"
          variants={imageReveal}
          initial="hidden"
          animate="show"
        >
          <div className="hero-visual-frame hero-visual-frame--glass">
            <SafeImage
              src={safeHero.imageUrl || HERO_IMAGE}
              fallbackSrcs={IMAGE_FALLBACK_CHAIN}
              alt={safeHero.imageAlt || 'Structural steel beams on site'}
              wrapperClassName="hero-img-wrap"
              imgClassName="hero-img"
              loading="eager"
            />

            <div className="hero-visual-badge">
              <div className="num">{safeHero.badgeNumber || '15+'}</div>
              <div className="lbl">{safeHero.badgeLabel || 'Years experience'}</div>
            </div>

            <div className="hero-visual-card">
              <div className="kicker">{safeHero.cardKicker || 'Bespoke steel solutions'}</div>
              <div className="meta">{safeHero.cardMeta || 'Fast quotes • Clear specs • Clean installs'}</div>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="hero-scroll-hint" aria-hidden>
        <span className="scroll-label">Scroll</span>
        <div className="scroll-line" />
      </div>
    </section>
  );
}
